import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@1trading/react';
import { CircleAlert } from 'lucide-react';
import { Fragment } from 'react';
import { LoadingPanel } from '@/components/ui/loading-panel';
import { StatusChip } from '@/components/ui/status-chip';
import { formatBrokerDateTime } from '@/features/brokers/batches/batch-formatters';
import { useBrokerFeeBands } from '@/hooks/queries/brokers/use-broker-detail';
import type { VolumeTier, WireFeeBand } from '@/services/api/brokers';
import { CHIP_XS_CLASS, FEE_BAND_VOLUME_CHIP_CLASS, feeBandStateVariant, formatFeeBandFee } from './broker-detail-format';
import { ErrorPanel, LoadMoreButton } from './detail-panels';

const COLUMN_COUNT = 6;

const isVolumeTiered = (band: WireFeeBand): band is WireFeeBand & { fee_structure: VolumeTier[] } => band.type === 'VOLUME' && Array.isArray(band.fee_structure);

/** Nested full-width row with the compact volume-tier table. */
const VolumeTiersRow = ({ tiers }: { tiers: VolumeTier[] }) => (
  <TableRow className="bg-muted/40 hover:bg-muted/40">
    <TableCell colSpan={COLUMN_COUNT}>
      <div className="flex flex-col gap-2 py-1">
        <span className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Volume Tiers</span>
        <Table className="w-max min-w-72">
          <TableHeader>
            <TableRow>
              <TableHead>Volume Threshold</TableHead>
              <TableHead>Maker Fee (bps)</TableHead>
              <TableHead>Taker Fee (bps)</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {tiers.map((tier) => (
              <TableRow key={tier.volume}>
                <TableCell className="tabular-nums">{tier.volume.toLocaleString()}</TableCell>
                <TableCell className="tabular-nums">{tier.maker_fee_bps}</TableCell>
                <TableCell className="tabular-nums">{tier.taker_fee_bps}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </TableCell>
  </TableRow>
);
VolumeTiersRow.displayName = 'VolumeTiersRow';

interface FeeBandsTabProps {
  brokerId: string;
}

/**
 * Read-only — fee-band review/approval happens through the broker-alert flow
 * (alert-actions/broker-fee-band-review.tsx), not on this page.
 * Row expansions (volume tiers, rejection reasons) are ALWAYS visible, not toggled,
 * so this uses the raw Table primitives instead of DataTable.
 */
export const FeeBandsTab = ({ brokerId }: FeeBandsTabProps) => {
  const query = useBrokerFeeBands(brokerId);

  if (query.isPending) return <LoadingPanel />;
  // Full panel only when nothing loaded; a failed Load More keeps the rows on
  // screen with the error inline below (isError with retained data).
  if (query.isError && !query.data) return <ErrorPanel error={query.error} onRetry={() => query.refetch()} title="Failed to fetch fee bands" />;

  const bands = query.data ?? [];

  return (
    <div className="flex flex-col gap-3">
      <div className="rounded-md border">
        <Table className="w-max min-w-full">
          <TableHeader>
            <TableRow>
              <TableHead>Name</TableHead>
              <TableHead>Type</TableHead>
              <TableHead>State</TableHead>
              <TableHead>Maker Fee (bps)</TableHead>
              <TableHead>Taker Fee (bps)</TableHead>
              <TableHead>Created</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {bands.length === 0 ? (
              <TableRow>
                <TableCell className="h-24 text-center text-muted-foreground" colSpan={COLUMN_COUNT}>
                  No fee bands found.
                </TableCell>
              </TableRow>
            ) : (
              bands.map((band) => (
                <Fragment key={band.fee_band_id}>
                  <TableRow>
                    <TableCell className="font-medium">{band.name}</TableCell>
                    <TableCell>
                      {band.type === 'VOLUME' ? (
                        <StatusChip className={`${CHIP_XS_CLASS} ${FEE_BAND_VOLUME_CHIP_CLASS}`}>VOLUME</StatusChip>
                      ) : (
                        <StatusChip className={CHIP_XS_CLASS} variant="info">
                          {band.type}
                        </StatusChip>
                      )}
                    </TableCell>
                    <TableCell>
                      <StatusChip className={CHIP_XS_CLASS} variant={feeBandStateVariant(band.state)}>
                        {band.state}
                      </StatusChip>
                    </TableCell>
                    <TableCell className="tabular-nums">{formatFeeBandFee(band, 'maker_fee_bps')}</TableCell>
                    <TableCell className="tabular-nums">{formatFeeBandFee(band, 'taker_fee_bps')}</TableCell>
                    <TableCell className="whitespace-nowrap tabular-nums">{formatBrokerDateTime(band.created_at)}</TableCell>
                  </TableRow>
                  {isVolumeTiered(band) ? <VolumeTiersRow tiers={band.fee_structure} /> : null}
                  {band.rejection_reason ? (
                    <TableRow className="bg-red-500/10 hover:bg-red-500/10">
                      <TableCell colSpan={COLUMN_COUNT}>
                        <span className="flex items-center gap-2 text-sm text-red-500">
                          <CircleAlert aria-hidden className="size-4 shrink-0" />
                          Rejection reason: {band.rejection_reason}
                        </span>
                      </TableCell>
                    </TableRow>
                  ) : null}
                </Fragment>
              ))
            )}
          </TableBody>
        </Table>
      </div>
      {query.isError ? (
        <ErrorPanel error={query.error} onRetry={() => query.refetch()} title="Failed to fetch fee bands" />
      ) : (
        <LoadMoreButton hasNextPage={query.hasNextPage} isFetchingNextPage={query.isFetchingNextPage} onLoadMore={() => query.fetchNextPage()} />
      )}
    </div>
  );
};
FeeBandsTab.displayName = 'FeeBandsTab';
